// src/services/eventSuppliersService.js
// Acceso a datos de los proveedores de cada evento (event_suppliers) y a lo
// que ve un proveedor al entrar (supplier_events()).
//
// Ver migración supabase/migrations/20261029_supplier_access.sql. Las reglas
// (servicios, estados, qué cambia al guardar) están en src/domain/suppliers.js.
// El equipo de cada línea lo pone la base de datos a partir del evento.
//
// Concurrencia: se guarda comparando con las líneas leídas al abrir la ficha.
// Una línea solo se cambia o se borra si su updated_at sigue siendo el leído,
// y un servicio solo se añade si nadie lo ha añadido entretanto. Si no, se
// lanza SupplierConflictError y no se pisa lo que haya guardado otra persona.
// El guardado no es atómico: si falla a mitad, el mensaje pide recargar.

import { getSupabaseClient } from "./supabaseClient.js";
import { planSupplierChanges, sortSupplierLines } from "../domain/suppliers.js";

const COLUMNS = "id, event_id, service, contact_id, status, notes_for_supplier, updated_at";

// Postgres: violación de un índice único.
const UNIQUE_VIOLATION = "23505";

const CONFLICT_MESSAGE = "Otra persona ha cambiado los proveedores de este evento. Recarga la ficha y vuelve a intentarlo.";

export class SupplierConflictError extends Error {
  constructor(message = CONFLICT_MESSAGE) {
    super(message);
    this.name = "SupplierConflictError";
    this.code = "conflict";
  }
}

function fail(error, context) {
  if (error?.code === UNIQUE_VIOLATION) throw new SupplierConflictError();
  const e = new Error(`${context}: ${error?.message || error}`);
  e.code = error?.code;
  e.raw = error;
  throw e;
}

export async function listEventSuppliers(eventId) {
  if (!eventId) return [];
  const { data, error } = await getSupabaseClient()
    .from("event_suppliers")
    .select(COLUMNS)
    .eq("event_id", eventId);
  if (error) fail(error, "No se pudieron leer los proveedores del evento");
  return sortSupplierLines(data || []);
}

// Producción: las líneas de varios eventos en una sola consulta, agrupadas
// por evento.
export async function listSuppliersForEvents(eventIds) {
  const map = new Map();
  if (!eventIds?.length) return map;
  const { data, error } = await getSupabaseClient()
    .from("event_suppliers")
    .select(COLUMNS)
    .in("event_id", eventIds);
  if (error) fail(error, "No se pudieron leer los proveedores de los eventos");
  for (const row of data || []) {
    if (!map.has(row.event_id)) map.set(row.event_id, []);
    map.get(row.event_id).push(row);
  }
  for (const [id, rows] of map) map.set(id, sortSupplierLines(rows));
  return map;
}

// loadedRows: las líneas tal como se leyeron al abrir la ficha.
// desired: { [service]: { contact_id, status?, notes_for_supplier? } | null }.
export async function saveEventSuppliers(eventId, loadedRows, desired) {
  if (!eventId) throw new Error("Falta el evento para guardar los proveedores.");
  const supabase = getSupabaseClient();
  const { inserts, updates, deletes } = planSupplierChanges(loadedRows, desired);
  const total = inserts.length + updates.length + deletes.length;
  let done = 0;
  const context = () => done
    ? "Se guardó solo una parte de los proveedores. Recarga la ficha"
    : "No se pudieron guardar los proveedores del evento";
  const expectOne = (data) => {
    if (!data?.length) throw new SupplierConflictError();
    done += 1;
  };

  for (const { id, updated_at } of deletes) {
    const { data, error } = await supabase.from("event_suppliers")
      .delete().eq("id", id).eq("updated_at", updated_at).select("id");
    if (error) fail(error, context());
    expectOne(data);
  }
  for (const { id, updated_at, changes } of updates) {
    const { data, error } = await supabase.from("event_suppliers")
      .update(changes).eq("id", id).eq("updated_at", updated_at).select("id");
    if (error) fail(error, context());
    expectOne(data);
  }
  if (inserts.length) {
    const rows = inserts.map(line => ({ event_id: eventId, ...line }));
    const { error } = await supabase.from("event_suppliers").insert(rows);
    if (error) fail(error, context());
    done += inserts.length;
  }

  return { inserted: inserts.length, updated: updates.length, deleted: deletes.length, total };
}

// Bolos del proveedor que ha iniciado sesión: nombre, fecha, lugar y sus
// servicios. Para cualquier otro rol devuelve una lista vacía.
export async function listMySupplierEvents() {
  const { data, error } = await getSupabaseClient().rpc("supplier_events");
  if (error) fail(error, "No se pudieron leer tus bolos");
  return data || [];
}

// Panel del músico: quién pone tarima, luces y sonido en un bolo suyo
// (crew_event_suppliers, migración 20261029c). Solo nombre, estado y nota.
export async function listCrewEventSuppliers(eventId) {
  if (!eventId) return [];
  const { data, error } = await getSupabaseClient().rpc("crew_event_suppliers", { p_event_id: eventId });
  if (error) fail(error, "No se pudieron leer los proveedores del bolo");
  return data || [];
}
